import { AuthAPI } from "@repo/api/auth";
import { UserAPI } from "@repo/api/user";
import { useAuth } from "@repo/context/auth/auth";

/**
 * 自动获取UID的API调用工具
 * 这些函数会自动从UserInfo上下文中获取UID，无需手动传入
 */

/**
 * 更新密码（自动获取UID）
 */
export function useUpdatePassword() {
  const { userInfo } = useAuth();
  return {
    updatePassword: async (password: string, sessionCode: string) => {
      if (!userInfo?.uid) {
        throw new Error("用户未登录，无法获取UID");
      }
      return AuthAPI.updatePassword(password, sessionCode, userInfo.uid);
    },
    uid: userInfo?.uid,
  };
}

/**
 * 更新手机号（自动获取UID）
 */
export function useUpdatePhone() {
  const { userInfo } = useAuth();
  return {
    updatePhone: async (
      phoneNumber: string,
      smsCode: string,
      countryCode: string,
      sessionCode: string,
    ) => {
      if (!userInfo?.uid) {
        throw new Error("用户未登录，无法获取UID");
      }
      return UserAPI.updatePhone(
        userInfo.uid,
        phoneNumber,
        smsCode,
        countryCode,
        sessionCode,
      );
    },
    uid: userInfo?.uid,
  };
}

/**
 * 提交实名信息（自动获取UID）
 */
export function useSubmitRealName() {
  const { userInfo } = useAuth();
  return {
    submitRealName: async (realName: string, idNumber: string, usefor: string) => {
      if (!userInfo?.uid) {
        throw new Error("用户未登录，无法获取UID");
      }
      return AuthAPI.getRecoverySessionByRealID(
        userInfo.uid,
        realName,
        idNumber,
        usefor,
      );
    },
    uid: userInfo?.uid,
  };
}

/**
 * 提交实名信息（支持URL参数UID，用于找回账号场景）
 * 优先使用URL参数中的UID，其次使用上下文中的UID
 */
export function useSubmitRealNameWithParam() {
  const { userInfo } = useAuth();
  return {
    submitRealName: async (
      uidParam: string | null | undefined,
      realName: string,
      idNumber: string,
      usefor: string,
    ) => {
      const uid = uidParam || userInfo?.uid;
      if (!uid) {
        throw new Error("无法获取用户信息，请检查登录状态或重新开始");
      }
      return AuthAPI.getRecoverySessionByRealID(uid, realName, idNumber, usefor);
    },
    uid: userInfo?.uid,
  };
}

/**
 * 短信验证相关API（支持URL参数UID，用于找回账号场景）
 * 优先使用URL参数中的UID，其次使用上下文中的UID
 */
export function useSmsVerificationWithParam() {
  const { userInfo } = useAuth();
  return {
    getSMS: async (biz: string) => {
      return AuthAPI.getSMS(biz);
    },
    submitSmsCode: async (code: string, biz: string) => {
      return AuthAPI.submitSmsCode(code, biz);
    },
    uid: userInfo?.uid,
  };
}

/**
 * 通用UID检查hook
 */
export function useUIDRequired() {
  const { userInfo } = useAuth();
  return {
    uid: userInfo?.uid,
    isLoggedIn: !!userInfo?.uid,
    requireLogin: () => {
      if (!userInfo?.uid) {
        throw new Error("此操作需要登录");
      }
      return userInfo.uid;
    },
  };
}

/**
 * 带UID验证的API调用示例
 */
export function useUIDApiCall() {
  const { uid, requireLogin } = useUIDRequired();

  const callApiWithUID = async (
    apiFunction: (uid: string, ...args: unknown[]) => Promise<unknown>,
    ...args: unknown[]
  ) => {
    const currentUid = requireLogin();
    return apiFunction(currentUid, ...args);
  };

  return {
    uid,
    callApiWithUID,
  };
}
